import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { categoryService } from '../../../services/categoryService';

const BusinessInfoSection = ({ formData, setFormData, errors }) => {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Load categories from database
  useEffect(() => {
    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        const { data, error } = await categoryService?.getCategories();
        if (error) {
          console.error('Failed to load categories:', error);
          // Fallback to static options
          setCategories([
            { value: '', label: 'Select Business Category' },
            { value: 'wellness-center', label: 'Wellness Center' },
            { value: 'yoga-studio', label: 'Yoga Studio' },
            { value: 'holistic-clinic', label: 'Holistic Clinic' },
            { value: 'massage-therapy', label: 'Massage Therapy' },
            { value: 'energy-healing', label: 'Energy Healing Center' },
            { value: 'meditation-center', label: 'Meditation Center' },
            { value: 'nutrition-wellness', label: 'Nutrition & Wellness' },
            { value: 'spiritual-center', label: 'Spiritual Center' },
            { value: 'holistic-practitioner', label: 'Individual Practitioner' },
            { value: 'retreat-center', label: 'Retreat Center' }
          ]);
        } else {
          const categoryOptions = [
            { value: '', label: 'Select Business Category' },
            ...data?.map(cat => ({
              value: cat?.id,
              label: cat?.name
            }))
          ];
          setCategories(categoryOptions);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, []);

  const handleLogoUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          logo: file,
          logoPreview: e?.target?.result
        }));
      };
      reader?.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: null,
      logoPreview: null
    }));
  };

  const addKeyFeature = () => {
    setFormData(prev => ({
      ...prev,
      keyFeatures: [...prev?.keyFeatures, '']
    }));
  };

  const updateKeyFeature = (index, value) => {
    setFormData(prev => ({
      ...prev,
      keyFeatures: prev?.keyFeatures?.map((feature, i) => 
        i === index ? value : feature
      )
    }));
  };

  const removeKeyFeature = (index) => {
    if (formData?.keyFeatures?.length > 1) {
      setFormData(prev => ({
        ...prev,
        keyFeatures: prev?.keyFeatures?.filter((_, i) => i !== index)
      }));
    }
  };

  const addCertification = () => {
    setFormData(prev => ({
      ...prev,
      certifications: [...prev?.certifications, {
        name: '',
        issuingBody: '',
        year: '',
        image: null
      }]
    }));
  };

  const updateCertification = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev?.certifications?.map((cert, i) => 
        i === index ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const removeCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev?.certifications?.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Building2" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Business Information
            </h2>
            <p className="text-muted-foreground text-sm">
              Tell us about your holistic business
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Business Logo */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-foreground mb-2">
              Business Logo
              <span className="text-muted-foreground text-xs ml-1">(recommended)</span>
            </label>
            
            <div className="flex items-start space-x-4">
              {/* Logo Preview */}
              <div className="w-20 h-20 border-2 border-dashed border-border rounded-lg flex items-center justify-center bg-muted/50">
                {formData?.logoPreview ? (
                  <img
                    src={formData?.logoPreview}
                    alt="Logo preview"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <Icon name="Image" size={24} className="text-muted-foreground" />
                )}
              </div>

              {/* Upload Controls */}
              <div className="flex-1 space-y-2">
                <div className="flex space-x-2">
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('logo-upload')?.click()}
                    iconName="Upload"
                    iconPosition="left"
                  >
                    Upload Logo
                  </Button>
                  {formData?.logoPreview && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={removeLogo}
                      iconName="Trash2"
                      iconPosition="left"
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG up to 2MB. Recommended size: 200x200px
                </p>
              </div>
            </div>
          </div>

          {/* Business Name */}
          <div className="md:col-span-2">
            <Input
              label="Business Name"
              placeholder="e.g., Serenity Wellness Centre"
              value={formData?.businessName || ''}
              onChange={(e) => setFormData(prev => ({...prev, businessName: e?.target?.value}))}
              error={errors?.businessName}
              required
            />
          </div>

          {/* Business Category */}
          <div className="md:col-span-1">
            <Select
              label="Business Category"
              value={formData?.category || ''}
              onChange={(value) => setFormData(prev => ({...prev, category: value}))}
              options={categories}
              error={errors?.category}
              required
              loading={categoriesLoading}
            />
          </div>

          {/* Star Rating */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-foreground mb-2">
              Star Rating
              <span className="text-muted-foreground text-xs ml-1">(if you have one)</span>
            </label>
            <div className="flex items-center space-x-2">
              <Input
                type="number"
                min="1"
                max="5"
                step="0.1"
                placeholder="4.5"
                value={formData?.rating || ''}
                onChange={(e) => setFormData(prev => ({...prev, rating: e?.target?.value}))}
                className="w-20"
              />
              <span className="text-sm text-muted-foreground">stars</span>
              <Input
                type="number"
                min="0"
                placeholder="123"
                value={formData?.reviewCount || ''}
                onChange={(e) => setFormData(prev => ({...prev, reviewCount: parseInt(e?.target?.value) || 0}))}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">reviews</span>
            </div>
          </div>

          {/* Business Description (About) */}
          <div className="md:col-span-2">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                About Your Business
                <span className="text-destructive ml-1">*</span>
              </label>
              <textarea
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none ${
                  errors?.businessDescription 
                    ? 'border-destructive focus:border-destructive focus:ring-destructive/20' :'border-border'
                }`}
                placeholder="Describe your business, your approach, and what makes you unique..."
                value={formData?.businessDescription || ''}
                onChange={(e) => setFormData(prev => ({...prev, businessDescription: e?.target?.value}))}
              />
              {errors?.businessDescription && (
                <span className="text-sm text-destructive">{errors?.businessDescription}</span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Key Features Section */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Star" size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground">Key Features</h3>
              <p className="text-muted-foreground text-sm">
                Highlight what makes your business special
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={addKeyFeature}
            iconName="Plus"
            iconPosition="left"
          >
            Add Feature
          </Button>
        </div>

        <div className="space-y-3">
          {formData?.keyFeatures?.map((feature, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Input
                placeholder="e.g., Experienced practitioners"
                value={feature}
                onChange={(e) => updateKeyFeature(index, e?.target?.value)}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeKeyFeature(index)}
                iconName="X"
                disabled={formData?.keyFeatures?.length <= 1}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Certifications Section */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Award" size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground">
                Certifications & Qualifications
              </h3>
              <p className="text-muted-foreground text-sm">
                Add your professional credentials
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={addCertification}
            iconName="Plus"
            iconPosition="left"
          >
            Add Certification
          </Button>
        </div>

        {formData?.certifications?.length > 0 ? (
          <div className="space-y-4">
            {formData?.certifications?.map((cert, index) => (
              <div key={index} className="p-4 border border-border rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    placeholder="Certification Name"
                    value={cert?.name}
                    onChange={(e) => updateCertification(index, 'name', e?.target?.value)}
                  />
                  <Input
                    placeholder="Issuing Body"
                    value={cert?.issuingBody}
                    onChange={(e) => updateCertification(index, 'issuingBody', e?.target?.value)}
                  />
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      placeholder="Year"
                      value={cert?.year}
                      onChange={(e) => updateCertification(index, 'year', e?.target?.value)}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCertification(index)}
                      iconName="Trash2"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Award" size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No certifications added yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessInfoSection;